/**
 * Cercare l'articolo: **un componente solo, in due posti**.
 *
 * Sta nella zona gesto quando non c'è nessun capo agganciato — è il primo gesto
 * del banco — e dentro il pannello `?` quando un capo c'è già e la zona mostra
 * le cassette. Due posti, un comportamento: chi impara a cercare qui sa cercare
 * anche di là, e non ci sono due ricerche da tenere allineate.
 *
 * Perché si cerca per nome e non si legge un codice: lo SKU non esiste. Lo
 * componiamo noi, DecoNetwork non ha un campo da cui leggerlo, e sulle etichette
 * dei capi c'è l'EAN del fornitore o un codice stile, mai il nostro — l'unica
 * etichetta che lo porta è quella della cassetta, che esce dopo l'aggancio
 * (`docs/superpowers/specs/2026-08-26-il-nome-al-posto-del-codice-design.md`).
 *
 * Tre proprietà, e sono quelle che lo rendono riusabile:
 *
 * 1. **è presentazionale.** Riceve le righe, l'arrivo agganciato e cosa fare
 *    quando se ne sceglie una: non conosce né la postazione, né il token, né
 *    react-query. Per questo si prova senza montare la rete;
 * 2. **il filtro è locale.** Le righe attese sono già in pagina dentro
 *    `BenchView.righe` — la finestra intera, non solo l'arrivo agganciato — e non
 *    c'è niente da chiedere. Una ricerca che va in rete a ogni tasto premuto
 *    renderebbe lento il banco proprio mentre l'operatrice ha le mani occupate,
 *    e DecoNetwork non saprebbe rispondere: `manage_products/find` torna zero su
 *    ogni campo, nome compreso;
 * 3. **la regola sta nel dominio.** Qui si chiede a `searchLines` e non si
 *    riscrive niente: cosa combacia, cosa si scarta e quale ordine sopravvive
 *    sono decisi là, dove un test li raggiunge senza React.
 *
 * I risultati escono **dal PO più vecchio**: la prima riga è quella che
 * l'anzianità sceglierebbe. Prenderne un'altra si può — chi ha la scatola in
 * mano sa qualcosa che la finestra non sa.
 *
 * Nessun conto si fa qui. Quanto manca per taglia arriva già deciso.
 */

import { useMemo, useState } from 'react';
import { searchLines, type LineView } from '@errebi/domain';

import { CapoInCabina } from './booth';
import { nomeRiga } from './line-label';

export function ArticleSearch({
  righe,
  arrivo,
  iniziale = '',
  intestazione,
  onAggancia,
}: {
  /** La finestra intera delle righe attese, dal PO più vecchio. */
  readonly righe: readonly LineView[];
  /** Il numero dell'arrivo già agganciato, per marcarne le righe. */
  readonly arrivo: string | null;
  /**
   * Quello che l'operatrice aveva già scritto altrove.
   *
   * Serve quando il pannello si apre da un campo dove si stava già cercando:
   * riscrivere le stesse parole vorrebbe dire pagare due volte la stessa
   * risposta.
   */
  readonly iniziale?: string;
  /**
   * Il titolo, quando serve. Nel pannello sì, perché è una finestra che si apre
   * e va detto cos'è; nella zona gesto no — la testata della zona lo dice già,
   * e ripeterlo mangerebbe una delle poche righe che quello spazio ha.
   */
  readonly intestazione?: { readonly titolo: string; readonly nota: string };
  readonly onAggancia: (ordine: string, riga: string) => void;
}) {
  const [cerca, setCerca] = useState(iniziale);

  const trovate = useMemo(() => searchLines(righe, cerca), [righe, cerca]);

  const chiesto = cerca.trim() !== '';

  return (
    <div className="attesi">
      {intestazione && (
        <div className="attesi__testa">
          <span className="gesto__k">{intestazione.titolo}</span>
          <span className="mod__hint">{intestazione.nota}</span>
        </div>
      )}

      <label className="campo">
        <span className="campo__k">Articolo, colore, cliente o fornitore</span>
        <input
          type="search"
          value={cerca}
          onChange={(e) => setCerca(e.target.value)}
          placeholder="es. royal iron grey"
          autoFocus
        />
      </label>

      <div className="attesi__elenco">
        {trovate.map((r) => (
          <button
            // Ordine di vendita più riga è la chiave di una riga in tutto il
            // resto del banco (`lineKey` nel dominio), e resta la chiave qui.
            key={`${r.ordine}|${r.riga}`}
            className={'trovata' + (r.acquisto === arrivo ? ' attesi__bolla--qui' : '')}
            // marcata, non solo colorata: chi legge con la tastiera deve sapere
            // qual è l'arrivo già agganciato senza guardare lo sfondo
            aria-current={r.acquisto === arrivo ? 'true' : undefined}
            onClick={() => onAggancia(r.ordine, r.riga)}
          >
            {/* La foto è il modo più rapido di distinguere due nomi che si
                somigliano: il verbale del 03/08 lo chiede per il fornitore
                Piper, «tre varianti molto simili». Il ripiego disegnato vale la
                metà dei capi, e lo gestisce già la cabina luce. */}
            <CapoInCabina riga={r} className="trovata__foto" />
            <span className="trovata__capo">
              {r.articolo}
              {r.colore ? ` · ${r.colore}` : ''}
            </span>
            <span className="trovata__cliente">{r.cliente}</span>
            <span className="trovata__doc">
              {nomeRiga(r)} · {r.fornitore === '' ? 'fornitore ignoto' : r.fornitore} · PO{' '}
              {r.acquisto}
            </span>
            <span className="trovata__conto">{descriviMancanti(r)}</span>
          </button>
        ))}

        {trovate.length === 0 && (
          <p className="attesi__vuoto">
            {chiesto
              ? `Nessuna riga attesa per «${cerca.trim()}». Prova col solo nome dell’articolo, ` +
                'o col cliente o il fornitore.'
              : 'Scrivi articolo e colore come stanno sull’etichetta del capo.'}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Quanto manca ancora su questa riga, taglia per taglia.
 *
 * È il numero che decide se la riga in mano è quella: due righe dello stesso
 * articolo si distinguono per chi le aspetta e per quanto ne resta. Le taglie
 * escono nell'ordine in cui la vista le ha messe — riordinarle qui sarebbe un
 * secondo parere su una cosa già decisa.
 */
function descriviMancanti(r: LineView): string {
  const voci = Object.entries(r.mancanti)
    .filter(([, quante]) => quante > 0)
    .map(([taglia, quante]) => `${taglia} ×${quante}`);
  return voci.length === 0 ? 'niente da ricevere' : voci.join(' · ');
}
